import { useState } from "react"
import { ChevronRight, Loader2, Wrench } from "lucide-react"
import { cn } from "../../lib/utils"
import type { ToolCall } from "./types"

/** Soften a tool id for display: `get-bookings-by-date` / `getBookingsByDate` → `Get bookings by date`. */
function label(name: string): string {
  const s = name
    .replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .trim()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function StateDot({ state }: { state: ToolCall["state"] }) {
  const cls =
    state === "error"
      ? "bg-destructive"
      : state === "result"
        ? "bg-emerald-500"
        : "bg-foreground/30"
  return <span className={cn("size-1.5 shrink-0 rounded-full", cls)} aria-hidden />
}

/**
 * All of a response's tool calls in ONE collapsible component. While the
 * response streams, the header shows the CURRENT tool (one at a time, updating
 * as each new call arrives); once done it collapses to "N tools". Expand to see
 * every tool that ran — names only, no outputs.
 */
export function ToolCallGroup({
  toolCalls,
  streaming,
}: {
  toolCalls: ToolCall[]
  streaming?: boolean
}) {
  const [open, setOpen] = useState(false)
  if (toolCalls.length === 0) return null

  const last = toolCalls[toolCalls.length - 1]
  const running = !!streaming && last.state !== "result" && last.state !== "error"
  const summary = running
    ? label(last.name)
    : `${toolCalls.length} tool${toolCalls.length === 1 ? "" : "s"}`

  return (
    <div className="w-full max-w-full overflow-hidden rounded-lg border border-black/10 text-left dark:border-white/10">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-foreground/5"
        aria-expanded={open}
      >
        {running ? (
          <Loader2 className="size-4 shrink-0 animate-spin text-foreground/70" />
        ) : (
          <Wrench className="size-4 shrink-0 text-foreground/70" />
        )}
        {/* Key the label by its text so it re-mounts and cross-fades/slides each
            time the current streaming tool changes. */}
        <span
          key={summary}
          className="min-w-0 truncate font-medium text-foreground duration-300 ease-out animate-in fade-in-0 slide-in-from-bottom-1"
        >
          {summary}
        </span>
        {/* Explicit "expand" affordance so it reads as clickable. */}
        <span className="ml-auto flex shrink-0 items-center gap-1 text-[0.7rem] text-muted-foreground">
          {open ? "Hide" : "Details"}
          <ChevronRight
            className={cn(
              "size-4 text-foreground/60 transition-transform duration-200 ease-out",
              open && "rotate-90",
            )}
          />
        </span>
      </button>
      {/* Smooth expand/collapse via a 0fr→1fr grid row (animates height without
          measuring, stays mounted so it eases both ways). */}
      <div
        className={cn(
          "grid transition-all duration-200 ease-out",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <ul className="space-y-1 border-t border-black/5 px-2.5 py-1.5 dark:border-white/5">
            {toolCalls.map((t, i) => (
              <li
                key={t.id || `${t.name}-${i}`}
                className="flex items-center gap-2 text-xs text-muted-foreground"
              >
                <StateDot state={t.state} />
                <span className="min-w-0 truncate">{label(t.name)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
